/**
 * The two missions flown against something that is not an aeroplane: burning
 * observation balloons, and bringing down a Zeppelin.
 *
 * Both are possible because both airships have real baked geometry —
 * `VOLUME.005/BALLOON.TBL` and `VOLUME.005/ZEPPELIN.TBL` — and because
 * `combat/targets.ts` gives them an integrity model the same gunnery solver can
 * shoot at. The balloon site now has its **archie** as well (`combat/flak.ts`,
 * AD-010), which is what makes the mission the trade it is meant to be: the
 * ingress altitude sits deliberately *above* the batteries' 1 372 m ceiling, so
 * the shooting starts when the player dives to the gasbag and stops when he
 * climbs away — the original's own geometry, and the reason the Mission Builder
 * warns authors to keep balloons at 5 000 ft or lower. A Zeppelin's own
 * defensive gunners are still not modelled and its port note says so.
 *
 * The ammunition choice is real. `PS.EXE`/`RBMB.EXE` name **regular** and
 * **incendiary** loadouts, historically because ordinary ball rounds pass
 * straight through a hydrogen bag without igniting it. Both missions here load
 * incendiary by default — the manual's own advice for both — but it is a
 * `MissionOptions.ammunition` choice like every other generator's, settable on
 * the Flight Assignments screen; docs/game/missions.md's open question about
 * where that choice lives is resolved by that field existing.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import { buildFlight, briefingLines, aerodromeStartLine, climbFrom, waypoint } from "./mission-build.ts";
import { missionContext, type MissionContext, type MissionOptions } from "./mission-options.ts";
import { scoutsFor } from "./mission-roster.ts";
import { assembleMission } from "./mission-assemble.ts";
import { homeAerodromeAt } from "./generators-scramble.ts";
import type { MissionBase, MissionFlak, MissionFlight, MissionSpec, MissionTarget } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";
import { OBJECTIVE_RADIUS_M } from "./generators-sortie.ts";

/** Observation balloons flew low and tethered; the raid comes in above them. */
const BALLOON_ALTITUDE_M = 1150;
const BALLOON_RAID_ALTITUDE_M = 1450;
/** Zeppelins raided high. High enough to matter, low enough that the tuning holds. */
const ZEPPELIN_ALTITUDE_M = 2700;

const FLAK_NOTE =
  "The balloon site carries a light battery of its own on top of the standing belt along the front. Envelope, cadence, aiming, burst placement, whether a shell hurts and how much all come from the original's `AA.DAT` and `s\\combat\\aa.c` (AD-010 §9 closed the last two). What is port-side is *how many* batteries stand where: the original authors every emplacement into its scenario stream, so this port places them at the density the shipped scenarios were measured to have.";

/**
 * The batteries around a balloon site. The original does not place flak from
 * the world data — no `.WLD` record references a flak object, and `aa.c` is
 * handed emplacements by the scenario — so a port generator has to place them,
 * and this is the placement the manual describes: *"most were heavily guarded
 * by rings of anti-aircraft guns deployed on the ground … the batteries
 * defending observation balloons were very effective because the gunners knew
 * the altitude of their balloons"* (`docs/game/manual/03-flight-reference.md`).
 *
 * **One `light` battery between the gasbags**, on top of the standing belt
 * along the front that `flak-placement.ts` gives every mission. The count has
 * been re-cut twice and both cuts were forced by the same measurement. A ring
 * of four made the sortie unwinnable on all ten seeds tried; two `heavy`
 * batteries then gave 12 of 24 while flak's own lethality was still a port
 * estimate. AD-010 §9 replaced that estimate with `aa.c`'s real geometry, which
 * connects on 6.4 % of shells rather than 2.5 %, and the same pair dropped the
 * sortie to 4 of 24 with the player lost 20 times — dominating, not
 * threatening. A single **light** battery keeps the manual's point, and its
 * 762 m reach is the whole of why: the guns are *at* the site, so the ingress
 * is free and only the dive costs anything. **15 of 24 seeds win** and the
 * player is lost 6 times, against 12 and 8 with the old pair of heavies — and
 * the raid is *more* eventful, not less, at 4.8 hits a sortie against 2.5.
 */
function balloonSiteFlak(line: number): readonly MissionFlak[] {
  return [
    {
      id: "flak-1",
      side: "hostile",
      // Between the two gasbags, so its 762 m reach covers both and little else.
      position: { x: 0, y: 0, z: line - 150 },
      batteryClass: "light",
    },
  ];
}

/** Two Drachen strung out along the enemy rear, both hostile, both tethered. */
function balloonPair(spread: number, line: number): readonly MissionTarget[] {
  return [
    {
      id: "balloon-1",
      kind: "balloon",
      side: "hostile",
      position: { x: -spread, y: BALLOON_ALTITUDE_M, z: line },
      headingRad: 0,
      label: "Drachen 1",
    },
    {
      id: "balloon-2",
      kind: "balloon",
      side: "hostile",
      position: { x: spread, y: BALLOON_ALTITUDE_M, z: line - 300 },
      headingRad: 0,
      label: "Drachen 2",
    },
  ];
}

interface BalloonSite {
  readonly spread: number;
  readonly line: number;
  readonly escorts: number;
  readonly escortAltitude: number;
  readonly enemyAircraft: AircraftKey;
  /** Set when `MissionOptions.aerodromeStart` asks the player's flight to launch from its own field. */
  readonly base?: MissionBase;
  readonly ammunition: "regular" | "incendiary";
}

/** The raider, with its belt and its run laid out, plus the guard overhead. */
function balloonRaidFlights(context: MissionContext, site: BalloonSite): MissionFlight[] {
  const { spread, line, escortAltitude, base } = site;
  const run = [
    waypoint({ x: -spread, y: BALLOON_ALTITUDE_M + 150, z: line }, "DRACHEN 1"),
    waypoint({ x: spread, y: BALLOON_ALTITUDE_M + 150, z: line - 300 }, "DRACHEN 2"),
    waypoint({ x: 0, y: BALLOON_RAID_ALTITUDE_M, z: 2600 }, "HOME"),
  ];
  return [
    buildFlight({
      id: "player",
      side: "friendly",
      aircraft: context.playerAircraft,
      callsign: "You",
      position: base ? base.position : { x: 0, y: BALLOON_RAID_ALTITUDE_M, z: 1400 },
      headingRad: 0,
      onGround: base ? true : undefined,
      ammunition: site.ammunition,
      waypoints: base ? [climbFrom(base, BALLOON_RAID_ALTITUDE_M), ...run] : run,
    }),
    ...Array.from({ length: site.escorts }, (_unused, index) =>
      buildFlight({
        id: `hostile-${index + 1}`,
        side: "hostile",
        aircraft: site.enemyAircraft,
        callsign: `Balloon guard ${index + 1}`,
        position: { x: spread - index * 400, y: escortAltitude + index * 90, z: line - 600 },
        headingRad: Math.PI,
        skill: index === 0 ? "veteran" : "regular",
        waypoints: [
          waypoint({ x: -spread, y: escortAltitude, z: line + 200 }, "ORBIT W"),
          waypoint({ x: spread, y: escortAltitude, z: line - 600 }, "ORBIT E"),
        ],
      }),
    ),
  ];
}

/**
 * **BALLOON BUSTING!** Two Drachen over the enemy rear, a standing patrol above
 * them, and a belt of incendiary. Burn both and get home.
 */
export function generateBalloonBusting(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "balloon-busting");
  const { draw } = context;
  const spread = draw.between(450, 750);
  const line = -1700 - draw.between(0, 500);
  const enemyAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const escortAltitude = BALLOON_RAID_ALTITUDE_M + 400;
  const escorts = draw.int(1, 2);
  const ammunition = options.ammunition ?? "incendiary";

  const targets = balloonPair(spread, line);
  const base = context.aerodromeStart ? homeAerodromeAt(context) : undefined;

  const flights = balloonRaidFlights(context, { spread, line, escorts, escortAltitude, enemyAircraft, base, ammunition });

  const objectives: readonly MissionObjective[] = [
    {
      kind: "destroyTargets",
      id: "burn-balloons",
      label: "Burn both observation balloons",
      role: "primary",
      count: 2,
      targetIds: ["balloon-1", "balloon-2"],
    },
    { kind: "returnToBase", id: "rtb", label: "Return across your own lines", role: "primary", flightId: "player" },
  ];

  return assembleMission(context, {
    catalogId: "balloon-busting",
    type: "balloonBusting",
    title: "Balloon busting",
    flights,
    targets,
    flak: balloonSiteFlak(line),
    objectives,
    homeBase: base,
    briefing: [
      ...(base ? [aerodromeStartLine(base)] : []),
      ...briefingLines({
        flightSize: 1,
        ownAircraft: context.playerAircraft,
        enemyCount: escorts,
        enemySkill: "veteran",
        enemyNation: context.enemyNation,
        enemyLeader: "the balloon guard",
        enemyAircraft,
        region: context.region,
        ownAltitudeM: base ? 0 : BALLOON_RAID_ALTITUDE_M,
        enemyAltitudeM: escortAltitude,
        weather: context.weather,
      }),
    ],
    portNotes: [
      FLAK_NOTE,
      "The balloon's winch is not modelled: a real crew hauled the envelope down the moment a scout appeared, and this one waits where it is.",
    ],
  });
}

/** The airship gets a sentence of its own before the standard three. */
function zeppelinBriefing(
  context: MissionContext,
  escortAircraft: AircraftKey,
  escortAltitude: number,
  base: MissionBase | undefined,
): string[] {
  return [
    ...(base ? [aerodromeStartLine(base)] : []),
    `A Zeppelin is running for home over ${context.region === "london" ? "the Channel" : "the lines"}, and you are the only thing between it and Germany.`,
    ...briefingLines({
      flightSize: 1,
      ownAircraft: context.playerAircraft,
      enemyCount: 1,
      enemySkill: "regular",
      enemyNation: context.enemyNation,
      enemyLeader: "the airship's escort",
      enemyAircraft: escortAircraft,
      region: context.region,
      ownAltitudeM: base ? 0 : ZEPPELIN_ALTITUDE_M + 300,
      enemyAltitudeM: escortAltitude,
      weather: context.weather,
    }),
  ];
}

/**
 * **HUNT A ZEPPELIN** (`RBMB.EXE`'s *Destroy a Zeppelin*). One airship — the
 * Mission Builder enforces *"You can have only one zeppelin"* — running for
 * home with a single scout in company. It is enormous, it is slow, and it takes
 * an implausible amount of shooting, which is roughly what the real thing was
 * like.
 */
export function generateZeppelinHunt(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "hunt-zeppelin");
  const { draw } = context;
  const drift = draw.between(-500, 500);
  const escortAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const escortAltitude = ZEPPELIN_ALTITUDE_M + 250;

  const targets: readonly MissionTarget[] = [
    {
      id: "zeppelin",
      kind: "zeppelin",
      side: "hostile",
      position: { x: drift, y: ZEPPELIN_ALTITUDE_M, z: -2800 },
      // Heading π drives it back over its own lines: the raid is done and it is
      // running for Germany, which is what puts a clock on the interception.
      headingRad: Math.PI,
      label: "Zeppelin L 31",
    },
  ];

  const base = context.aerodromeStart ? homeAerodromeAt(context) : undefined;
  const zeppelinRun = [
    waypoint({ x: drift, y: ZEPPELIN_ALTITUDE_M + 200, z: -2800 }, "ZEPPELIN"),
    waypoint({ x: 0, y: ZEPPELIN_ALTITUDE_M, z: 2400 }, "HOME"),
  ];

  const flights: MissionFlight[] = [
    buildFlight({
      id: "player",
      side: "friendly",
      aircraft: context.playerAircraft,
      callsign: "You",
      position: base ? base.position : { x: 0, y: ZEPPELIN_ALTITUDE_M + 300, z: 1200 },
      headingRad: 0,
      onGround: base ? true : undefined,
      ammunition: options.ammunition ?? "incendiary",
      waypoints: base ? [climbFrom(base, ZEPPELIN_ALTITUDE_M + 300), ...zeppelinRun] : zeppelinRun,
    }),
    buildFlight({
      id: "hostile-1",
      side: "hostile",
      aircraft: escortAircraft,
      callsign: "Escort",
      position: { x: drift + 400, y: escortAltitude, z: -3200 },
      headingRad: Math.PI,
      skill: "regular",
      waypoints: [waypoint({ x: drift, y: escortAltitude, z: -600 }, "SCREEN")],
    }),
  ];

  const objectives: readonly MissionObjective[] = [
    {
      kind: "destroyTargets",
      id: "burn-zeppelin",
      label: "Bring down the Zeppelin",
      role: "primary",
      count: 1,
      targetIds: ["zeppelin"],
    },
    { kind: "returnToBase", id: "rtb", label: "Return across your own lines", role: "primary", flightId: "player" },
  ];

  return assembleMission(context, {
    catalogId: "hunt-zeppelin",
    type: "destroyZeppelin",
    title: "Hunt a Zeppelin",
    flights,
    targets,
    objectives,
    homeBase: base,
    briefing: zeppelinBriefing(context, escortAircraft, escortAltitude, base),
    portNotes: [
      "The airship holds one course and one speed all the way home: it has no navigation of its own, so it cannot be turned back or outrun, only shot down.",
    ],
  });
}

/** Both strike missions want the player pointed at the thing they came to burn. */
export const STRIKE_APPROACH_RADIUS_M = OBJECTIVE_RADIUS_M;
